import React, { useEffect, useState, useMemo } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ShieldCheck,
  Plus,
  Edit,
  Trash2,
  Loader2,
  RefreshCw,
  Search,
  Sliders,
  CheckCircle2,
  Users,
  Percent,
  Eye,
  Key,
  Database,
  SlidersHorizontal,
} from "lucide-react";
import type { PermissionDefinition, RoleDetail } from "@/types";

const MODULE_DISPLAY_NAMES: Record<string, { label: string; icon: string }> = {
  lead: { label: "Leads", icon: "Users" },
  deal: { label: "Deals & Pipelines", icon: "TrendingUp" },
  quote: { label: "Quotes & Approvals", icon: "FileText" },
  product: { label: "Product Catalog", icon: "Box" },
  account: { label: "Accounts", icon: "Building" },
  contact: { label: "Contacts", icon: "UserCheck" },
  activity: { label: "Activities & Feed", icon: "Calendar" },
  report: { label: "Analytics & Reports", icon: "BarChart3" },
  setting: { label: "Settings", icon: "Settings" },
  user: { label: "User Management", icon: "UserPlus" },
  role: { label: "Role & RBAC", icon: "Shield" },
  audit: { label: "Audit & Changelog", icon: "History" },
};

const SCOPE_RESOURCES = [
  { key: "lead", label: "Leads Visibility", desc: "Controls which lead records this role can view and access" },
  { key: "deal", label: "Deals Visibility", desc: "Controls pipeline board deals visibility across the organization" },
  { key: "account", label: "Accounts Visibility", desc: "Controls company/account profile viewing scope" },
  { key: "contact", label: "Contacts Visibility", desc: "Controls contact cards visibility" },
  { key: "quote", label: "Quotations Visibility", desc: "Controls pricing quotation and estimate visibility" },
  { key: "report", label: "Reports & Analytics", desc: "Controls performance metric visibility in dashboards" },
];

export function RolesPage() {
  const [roles, setRoles] = useState<RoleDetail[]>([]);
  const [catalog, setCatalog] = useState<PermissionDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Role CRUD Dialog states
  const [addOpen, setAddOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleDetail | null>(null);
  const [deleteRole, setDeleteRole] = useState<RoleDetail | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [discountLimit, setDiscountLimit] = useState<number>(0);

  // RBAC Matrix Modal State
  const [rbacRole, setRbacRole] = useState<RoleDetail | null>(null);
  const [activeTab, setActiveTab] = useState<"permissions" | "scopes" | "limits">("permissions");
  const [selectedModule, setSelectedModule] = useState<string>("lead");
  const [rolePermissions, setRolePermissions] = useState<Set<string>>(new Set());
  const [roleScopes, setRoleScopes] = useState<Record<string, string>>({});
  const [roleDiscountLimit, setRoleDiscountLimit] = useState<number>(0);
  const [savingRbac, setSavingRbac] = useState(false);

  const { success, error } = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesRes, catalogRes] = await Promise.all([
        api.get("/api/UserRole/List"),
        api.get("/api/UserRole/PermissionsCatalog"),
      ]);

      const allRoles = unwrapArray<RoleDetail>(rolesRes);
      setRoles(allRoles.filter((r) => r.code?.toUpperCase() !== "SUPER_ADMIN"));

      const catList = unwrapArray<PermissionDefinition>(catalogRes);
      setCatalog(catList);
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load roles & permissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRoles = useMemo(() => {
    return roles.filter((r) => {
      const roleName = (r.name || "").toLowerCase();
      const roleCode = (r.code || "").toLowerCase();
      const q = search.toLowerCase();
      return roleName.includes(q) || roleCode.includes(q);
    });
  }, [roles, search]);

  // Group catalog by module
  const catalogByModule = useMemo(() => {
    const map: Record<string, PermissionDefinition[]> = {};
    catalog.forEach((item) => {
      const m = item.module || "other";
      if (!map[m]) map[m] = [];
      map[m].push(item);
    });
    return map;
  }, [catalog]);

  const moduleKeys = useMemo(() => Object.keys(catalogByModule), [catalogByModule]);

  // Handle Add Role
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) {
      error("Validation Error", "Name and code are required");
      return;
    }

    setActionLoading(true);
    try {
      const sanitizedLimit = Math.min(100, Math.max(0, Number(discountLimit) || 0));
      await api.post("/api/UserRole/Insert", {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        discount_limit_pct: sanitizedLimit,
      });
      success("Role Created", "Security role created successfully");
      setAddOpen(false);
      setName("");
      setCode("");
      setDiscountLimit(0);
      await loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Failed to create role");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Edit Role Basic Info
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editRole) return;

    setActionLoading(true);
    try {
      const sanitizedLimit = Math.min(100, Math.max(0, Number(discountLimit) || 0));
      await api.put(`/api/UserRole/Update/${editRole.id}`, {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        discount_limit_pct: sanitizedLimit,
      });
      success("Role Updated", "Security role updated successfully");
      setEditRole(null);
      await loadData();
    } catch (err: any) {
      error("Update Failed", err.message || "Failed to update role");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Delete Role
  const handleDelete = async () => {
    if (!deleteRole) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/UserRole/Delete/${deleteRole.id}`);
      success("Role Deleted", "Security role removed successfully");
      setDeleteRole(null);
      await loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete role");
    } finally {
      setActionLoading(false);
    }
  };

  // Open RBAC Matrix Modal
  const openRbacModal = async (r: RoleDetail) => {
    setRbacRole(r);
    setActiveTab("permissions");
    setSelectedModule(moduleKeys[0] || "lead");

    try {
      const res = await api.get(`/api/UserRole/RolePermissions/${r.id}`);
      const data = res?.data || res;
      setRolePermissions(new Set(data.permissions || []));
      setRoleScopes(data.data_scopes || {});
      setRoleDiscountLimit(Number(data.discount_limit_pct || 0));
    } catch (err: any) {
      // Fallback to role record state
      setRolePermissions(new Set(r.permissions || []));
      setRoleScopes(r.data_scopes || {});
      setRoleDiscountLimit(Number(r.discount_limit_pct || 0));
    }
  };

  // Toggle single permission
  const togglePermission = (key: string) => {
    const next = new Set(rolePermissions);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setRolePermissions(next);
  };

  // Toggle entire module
  const toggleModulePermissions = (module: string) => {
    const perms = catalogByModule[module] || [];
    const allSelected = perms.every((p) => rolePermissions.has(p.key));
    const next = new Set(rolePermissions);

    if (allSelected) {
      perms.forEach((p) => next.delete(p.key));
    } else {
      perms.forEach((p) => next.add(p.key));
    }
    setRolePermissions(next);
  };

  // Set resource scope
  const setScope = (resource: string, scope: string) => {
    setRoleScopes((prev) => ({ ...prev, [resource]: scope }));
  };

  // Save RBAC settings
  const handleSaveRbac = async () => {
    if (!rbacRole) return;
    setSavingRbac(true);

    try {
      await api.put(`/api/UserRole/UpdatePermissions/${rbacRole.id}`, {
        permissions: Array.from(rolePermissions),
        data_scopes: roleScopes,
        discount_limit_pct: Number(roleDiscountLimit) || 0,
      });

      success("RBAC Updated", `Permissions and scopes for ${rbacRole.name} updated successfully`);
      setRbacRole(null);
      await loadData();
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to update role permissions");
    } finally {
      setSavingRbac(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <ShieldCheck className="size-6 text-indigo-600 dark:text-indigo-400" />
            Roles & Enterprise RBAC Architecture
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure fine-grained permissions, hierarchical data scopes, and commercial limits across the organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="h-9 gap-1.5"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setName("");
              setCode("");
              setDiscountLimit(0);
              setAddOpen(true);
            }}
            className="h-9 gap-1.5 shadow-sm bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Plus className="size-4" />
            Add Role
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border/70 bg-card p-4 shadow-xs flex items-center gap-3.5">
          <div className="size-10 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-lg">
            {roles.length}
          </div>
          <div>
            <div className="text-xs font-medium text-muted-foreground">Configured Roles</div>
            <div className="text-base font-semibold text-foreground">Custom & Standard Tiers</div>
          </div>
        </div>

        <div className="rounded-xl border border-border/70 bg-card p-4 shadow-xs flex items-center gap-3.5">
          <div className="size-10 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-lg">
            {catalog.length}
          </div>
          <div>
            <div className="text-xs font-medium text-muted-foreground">Permission Keys</div>
            <div className="text-base font-semibold text-foreground">Across {moduleKeys.length} Modules</div>
          </div>
        </div>

        <div className="rounded-xl border border-border/70 bg-card p-4 shadow-xs flex items-center gap-3.5">
          <div className="size-10 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <Database className="size-5" />
          </div>
          <div>
            <div className="text-xs font-medium text-muted-foreground">Data Scope Engine</div>
            <div className="text-base font-semibold text-foreground">Hierarchy CTE (Own/Team/All)</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
        <Input
          placeholder="Search roles by title or system code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-9 text-sm"
        />
      </div>

      {/* Table */}
      {loading ? (
        <TableSkeleton rows={4} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Role Title & Code</TableHead>
                <TableHead>Permissions Coverage</TableHead>
                <TableHead>Commercial Limit</TableHead>
                <TableHead>Lead Scope</TableHead>
                <TableHead>Deal Scope</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRoles.map((r) => {
                const permsCount = r.permissions?.length || r.permissions_count || 0;
                const leadScope = r.data_scopes?.lead || "own";
                const dealScope = r.data_scopes?.deal || "own";
                const discountPct = Number(r.discount_limit_pct || 0);

                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="size-9 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                          <ShieldCheck className="size-5" />
                        </div>
                        <div>
                          <div className="font-semibold text-sm text-foreground">
                            {r.name}
                          </div>
                          <code className="text-[11px] font-mono font-medium text-muted-foreground">
                            {r.code}
                          </code>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={permsCount > 0 ? "secondary" : "outline"}
                          className="font-mono text-xs font-semibold"
                        >
                          <Key className="size-3 mr-1 text-indigo-500" />
                          {permsCount} / {catalog.length}
                        </Badge>
                      </div>
                    </TableCell>

                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        <Percent className="size-3 mr-1 text-amber-500" />
                        Max {discountPct}% Discount
                      </Badge>
                    </TableCell>

                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium uppercase font-mono ${
                        leadScope === "all"
                          ? "bg-purple-500/10 text-purple-700 dark:text-purple-300"
                          : leadScope === "team"
                          ? "bg-blue-500/10 text-blue-700 dark:text-blue-300"
                          : "bg-slate-500/10 text-slate-700 dark:text-slate-300"
                      }`}>
                        {leadScope}
                      </span>
                    </TableCell>

                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium uppercase font-mono ${
                        dealScope === "all"
                          ? "bg-purple-500/10 text-purple-700 dark:text-purple-300"
                          : dealScope === "team"
                          ? "bg-blue-500/10 text-blue-700 dark:text-blue-300"
                          : "bg-slate-500/10 text-slate-700 dark:text-slate-300"
                      }`}>
                        {dealScope}
                      </span>
                    </TableCell>

                    <TableCell className="text-right space-x-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openRbacModal(r)}
                        className="h-8 gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/50"
                        title="Configure RBAC Permissions & Scopes"
                      >
                        <Sliders className="size-3.5" />
                        Permissions & Scopes
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => {
                          setEditRole(r);
                          setName(r.name);
                          setCode(r.code);
                          setDiscountLimit(Number(r.discount_limit_pct || 0));
                        }}
                        title="Edit Role Name/Code"
                      >
                        <Edit className="size-4 text-muted-foreground hover:text-foreground" />
                      </Button>
                      {!r.is_system && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setDeleteRole(r)}
                          title="Delete Role"
                        >
                          <Trash2 className="size-4 text-red-500 hover:text-red-700" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredRoles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">
                    No security roles found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialog: Add Role */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">Create Security Role</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Define a new organizational role with initial commercial limits
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4 sm:space-y-5 py-2">
            <div className="space-y-2">
              <Label>Role Name *</Label>
              <Input
                required
                placeholder="e.g. Regional Sales Director"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Role Code *</Label>
              <Input
                required
                placeholder="e.g. REGIONAL_DIRECTOR"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="font-mono uppercase"
              />
            </div>
            <div className="space-y-2">
              <Label>Max Discount Limit (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="1"
                placeholder="0"
                value={discountLimit}
                onChange={(e) => setDiscountLimit(Number(e.target.value))}
              />
              <span className="text-xs text-muted-foreground block">
                Max discount this role can authorize on deals and quotations without executive escalation.
              </span>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading} className="bg-primary hover:bg-primary/90 text-white">
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Create Role
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit Role Basic Info */}
      <Dialog open={!!editRole} onOpenChange={(open) => !open && setEditRole(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">Edit Security Role</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Modify role designation and authorization parameters
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 sm:space-y-5 py-2">
            <div className="space-y-2">
              <Label>Role Name *</Label>
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Role Code *</Label>
              <Input
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="font-mono uppercase"
              />
            </div>
            <div className="space-y-2">
              <Label>Max Discount Limit (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="1"
                value={discountLimit}
                onChange={(e) => setDiscountLimit(Number(e.target.value))}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditRole(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete Role */}
      <Dialog open={!!deleteRole} onOpenChange={(open) => !open && setDeleteRole(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg text-red-600 dark:text-red-400">Delete Security Role</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Are you sure you want to remove role{" "}
              <strong className="text-foreground">{deleteRole?.name}</strong> ({deleteRole?.code})? Users assigned this role will lose permissions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteRole(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Confirm Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* ENTERPRISE RBAC PERMISSIONS & DATA SCOPES MODAL                           */}
      {/* ========================================================================= */}
      <Dialog open={!!rbacRole} onOpenChange={(open) => !open && setRbacRole(null)}>
        <DialogContent className="sm:max-w-5xl md:max-w-6xl max-h-[92vh] flex flex-col p-0 overflow-hidden">
          {/* Header */}
          <div className="p-6 pb-4 border-b border-border bg-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-lg font-bold text-foreground">
                    RBAC Policy: {rbacRole?.name}
                  </DialogTitle>
                  <code className="px-2 py-0.5 rounded bg-muted font-mono text-xs font-semibold text-foreground">
                    {rbacRole?.code}
                  </code>
                </div>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  Configure the 3 security layers: Permissions, Recursive Data Scopes, and Limit & Field Rules
                </DialogDescription>
              </div>

              <div className="flex items-center gap-2 self-start sm:self-auto">
                <Badge variant="secondary" className="font-mono text-xs">
                  {rolePermissions.size} / {catalog.length} Permissions Active
                </Badge>
              </div>
            </div>

            {/* Layer Tabs */}
            <div className="flex items-center gap-2 mt-4 border-b border-border/50">
              <button
                type="button"
                onClick={() => setActiveTab("permissions")}
                className={`flex items-center gap-2 px-3.5 py-2 text-xs font-semibold border-b-2 transition-colors ${
                  activeTab === "permissions"
                    ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Key className="size-3.5" />
                Layer 1: Permission Matrix
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("scopes")}
                className={`flex items-center gap-2 px-3.5 py-2 text-xs font-semibold border-b-2 transition-colors ${
                  activeTab === "scopes"
                    ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Database className="size-3.5" />
                Layer 2: Data Scopes
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("limits")}
                className={`flex items-center gap-2 px-3.5 py-2 text-xs font-semibold border-b-2 transition-colors ${
                  activeTab === "limits"
                    ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <SlidersHorizontal className="size-3.5" />
                Layer 3: Limits & Field Rules
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* LAYER 1: PERMISSIONS MATRIX */}
            {activeTab === "permissions" && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Module Pill Navigation */}
                <div className="md:col-span-1 space-y-1 border-r border-border pr-3">
                  <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                    Modules
                  </div>
                  {moduleKeys.map((m) => {
                    const info = MODULE_DISPLAY_NAMES[m] || { label: m.toUpperCase() };
                    const modulePerms = catalogByModule[m] || [];
                    const activeInModule = modulePerms.filter((p) => rolePermissions.has(p.key)).length;
                    const isSelected = selectedModule === m;

                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setSelectedModule(m)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium flex items-center justify-between transition-colors ${
                          isSelected
                            ? "bg-indigo-600 text-white font-semibold shadow-xs"
                            : "text-foreground hover:bg-muted"
                        }`}
                      >
                        <span className="truncate">{info.label}</span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                            isSelected
                              ? "bg-white/20 text-white"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {activeInModule}/{modulePerms.length}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Permission Cards for Selected Module */}
                <div className="md:col-span-3 space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-border">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {MODULE_DISPLAY_NAMES[selectedModule]?.label || selectedModule.toUpperCase()} Permissions
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Toggle specific functional actions granted to this role
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => toggleModulePermissions(selectedModule)}
                      className="h-7 text-xs"
                    >
                      {(catalogByModule[selectedModule] || []).every((p) => rolePermissions.has(p.key))
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-2.5">
                    {(catalogByModule[selectedModule] || []).map((perm) => {
                      const checked = rolePermissions.has(perm.key);

                      return (
                        <div
                          key={perm.key}
                          onClick={() => togglePermission(perm.key)}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            checked
                              ? "border-indigo-500/50 bg-indigo-500/5 dark:bg-indigo-950/20"
                              : "border-border hover:border-border/80 bg-card hover:bg-muted/30"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {}} // handled by parent div
                            className="mt-0.5 size-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <code className="text-xs font-mono font-semibold text-foreground">
                                {perm.key}
                              </code>
                              {checked && (
                                <span className="inline-flex items-center text-[10px] font-semibold text-indigo-600 dark:text-indigo-400">
                                  <CheckCircle2 className="size-3 mr-0.5" />
                                  Granted
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {perm.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* LAYER 2: DATA SCOPES */}
            {activeTab === "scopes" && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl border border-blue-500/20 bg-blue-500/5 text-xs text-blue-900 dark:text-blue-200">
                  <div className="font-semibold flex items-center gap-1.5 mb-1">
                    <Database className="size-4 text-blue-600 dark:text-blue-400" />
                    Hierarchical Data Scoping Engine
                  </div>
                  Data scopes govern record visibility. <strong>Own</strong> limits reps to records assigned directly to them.{" "}
                  <strong>Team</strong> leverages recursive hierarchical trees via <code className="font-mono bg-blue-500/10 px-1 py-0.5 rounded">users.manager_id</code> (managers see direct and indirect subordinates).{" "}
                  <strong>All</strong> grants complete cross-departmental visibility. Single record checks return <strong>HTTP 404</strong> on unauthorized access to prevent ID enumeration.
                </div>

                <div className="space-y-3">
                  {SCOPE_RESOURCES.map((res) => {
                    const currentScope = roleScopes[res.key] || "own";

                    return (
                      <div
                        key={res.key}
                        className="p-4 rounded-lg border border-border bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                      >
                        <div>
                          <div className="text-sm font-semibold text-foreground">
                            {res.label}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {res.desc}
                          </p>
                        </div>

                        {/* Scope Selector Segmented Badges */}
                        <div className="flex items-center gap-1 bg-muted p-1 rounded-lg shrink-0">
                          {(["own", "team", "all"] as const).map((s) => {
                            const isSelected = currentScope === s;

                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => setScope(res.key, s)}
                                className={`px-3 py-1 rounded-md text-xs font-semibold capitalize font-mono transition-all ${
                                  isSelected
                                    ? s === "all"
                                      ? "bg-purple-600 text-white shadow-xs"
                                      : s === "team"
                                      ? "bg-blue-600 text-white shadow-xs"
                                      : "bg-slate-700 text-white shadow-xs"
                                    : "text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                {s}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* LAYER 3: LIMITS & FIELD RULES */}
            {activeTab === "limits" && (
              <div className="space-y-6">
                {/* Commercial Discount Limit */}
                <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Percent className="size-4 text-amber-500" />
                        Commercial Discount Authority Limit
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Defines the maximum discount percentage this role can authorize on deals and quotation line items.
                      </p>
                    </div>
                    <Badge variant="outline" className="font-mono text-sm px-2.5 py-1">
                      {roleDiscountLimit}% Max
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={roleDiscountLimit}
                      onChange={(e) => setRoleDiscountLimit(Number(e.target.value))}
                      className="max-w-xs"
                    />

                    {/* Quick Presets */}
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-[11px] text-muted-foreground">Presets:</span>
                      {[0, 5, 10, 15, 25, 50, 100].map((val) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setRoleDiscountLimit(val)}
                          className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                            roleDiscountLimit === val
                              ? "bg-indigo-600 text-white border-indigo-600 font-semibold"
                              : "border-border text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          {val}%
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Field-level Visibility Protection */}
                <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Eye className="size-4 text-indigo-500" />
                      Field-Level Security (Response Stripping)
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Sensitive commercial fields such as <code className="font-mono">cost_price</code> and <code className="font-mono">margin</code> are automatically sanitized by the backend FieldFilterInterceptor unless the user has explicit permission.
                    </p>
                  </div>

                  <div
                    onClick={() => togglePermission("product.view_cost")}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      rolePermissions.has("product.view_cost")
                        ? "border-emerald-500/50 bg-emerald-500/5 dark:bg-emerald-950/20"
                        : "border-border hover:border-border/80 bg-card hover:bg-muted/30"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={rolePermissions.has("product.view_cost")}
                      onChange={() => {}}
                      className="mt-0.5 size-4 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    />
                    <div>
                      <div className="text-xs font-semibold text-foreground">
                        Authorize View of Cost Price & Margins (<code>product.view_cost</code>)
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        When disabled, purchase costs, vendor acquisition prices, and internal gross margin percentages are automatically stripped from all JSON responses.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border bg-muted/20 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Changes take effect immediately and invalidate active Redis token caches.
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRbacRole(null)}
                disabled={savingRbac}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveRbac}
                disabled={savingRbac}
                className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs"
              >
                {savingRbac ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save RBAC Policy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
export default RolesPage;
